import { useEffect, useState } from 'react';
import { Mic, Square, Globe } from 'lucide-react';
import { Orb } from '../ui/orb';
import { Button } from '../ui/atoms/Button';
import { SegmentedButton } from '../ui/atoms/SegmentedButton';
import {
  useVoiceSession,
  type ConversationTurn,
  type SessionStatus,
} from '../../hooks/useVoiceSession';

interface VoiceSessionCardProps {
  jobDescription: string;
  accessCode: string;
  onSessionEnd?: (transcript: ConversationTurn[], audioBlob: Blob | null) => void;
  onStatusChange?: (status: SessionStatus) => void;
  onReset?: () => void;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString()}:${secs.toString().padStart(2, '0')}`;
}

function getStatusMessage(status: SessionStatus, agentStateText: string | null): string {
  switch (status) {
    case 'idle':
      return 'Ready to start';
    case 'connecting':
      return '';
    case 'active':
      return agentStateText ?? 'Session active';
    case 'ending':
      return 'Ending session...';
    case 'done':
      return 'Interview complete';
    case 'error':
      return '';
    default:
      return '';
  }
}

function getAgentStateText(agentState: 'listening' | 'talking' | 'thinking' | null): string | null {
  switch (agentState) {
    case 'listening':
      return 'Listening...';
    case 'talking':
      return 'AI is responding...';
    case 'thinking':
      return 'Processing...';
    case null:
      return null;
    default:
      return null;
  }
}

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
] as const;

export function VoiceSessionCard({
  jobDescription,
  accessCode,
  onSessionEnd,
  onStatusChange,
  onReset,
}: VoiceSessionCardProps) {
  const { state, startSession, endSession, resetSession, getInputVolume, getOutputVolume, getAudioBlob } = useVoiceSession();
  const [language, setLanguage] = useState<string>('en');

  useEffect(() => {
    onStatusChange?.(state.status);
  }, [state.status, onStatusChange]);

  useEffect(() => {
    if (state.status === 'done') {
      const timer = setTimeout(() => {
        onSessionEnd?.(state.transcript, getAudioBlob());
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [state.status, state.transcript, onSessionEnd, getAudioBlob]);

  const agentStateText = getAgentStateText(state.agentState);
  const statusMessage = getStatusMessage(state.status, agentStateText);
  const isIdle = state.status === 'idle' || state.status === 'error';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col overflow-hidden">
      {/* Orb */}
      <div className="flex-1 relative min-h-0 flex items-center justify-center">
        <div className="w-full h-full max-h-[320px]">
          <Orb
            agentState={state.agentState}
            colors={['#E5CBF6', '#D9ADFF']}
            seed={42}
            getInputVolume={getInputVolume}
            getOutputVolume={getOutputVolume}
          />
        </div>

        {/* Timer overlay */}
        {(state.status === 'active' || state.status === 'ending') && (
          <div className="absolute inset-0 flex items-end justify-center pb-4 pointer-events-none">
            <div className="bg-white/80 backdrop-blur-sm rounded-full px-4 py-1.5 shadow-sm">
              <span className="text-2xl font-mono font-bold tabular-nums text-dark-invirtus tracking-tight">
                {formatTime(state.secondsRemaining)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="px-6 pb-5 pt-2 flex flex-col items-center gap-2">
        {/* Language picker — only when idle */}
        {isIdle && (
          <div className="flex items-center gap-1.5 mb-1">
            <Globe className="w-3.5 h-3.5 text-dark-invirtus/40" aria-hidden />
            <SegmentedButton
              aria-label="Interview language"
              size="sm"
              appearance="filled"
              intent="secondary"
              value={language}
              onValueChange={setLanguage}
              options={LANGUAGES.map((lang) => ({
                value: lang.code,
                label: lang.label,
              }))}
            />
          </div>
        )}

        {statusMessage && (
          <p className="font-body text-dark-invirtus/40 text-xs">{statusMessage}</p>
        )}

        {isIdle && (
          <Button
            intent="primary"
            onClick={() => startSession(jobDescription, accessCode, language)}
            disabled={!jobDescription.trim()}
            className="gap-2"
          >
            <Mic className="w-4 h-4" />
            Start Interview
          </Button>
        )}

        {state.status === 'active' && (
          <Button intent="danger" onClick={endSession} className="gap-2">
            <Square className="w-4 h-4" />
            End Interview
          </Button>
        )}

        {state.status === 'connecting' && (
          <div className="flex items-center gap-2 text-dark-invirtus/40">
            <div className="w-4 h-4 border-2 border-[var(--color-purple)] border-t-transparent rounded-full animate-spin" />
            <span className="font-body text-xs">Connecting...</span>
          </div>
        )}

        {state.error && (
          <div className="w-full p-2.5 rounded-lg bg-[var(--color-light-red)] text-[var(--color-red)] text-xs text-left">
            {state.error}
          </div>
        )}

        {isIdle && !jobDescription.trim() && (
          <p className="text-[10px] font-body text-dark-invirtus/25">
            Add a job description to begin
          </p>
        )}

        {/* Done state — two options */}
        {state.status === 'done' && (
          <div className="flex items-center gap-2">
            <Button
              intent="secondary"
              type="outlined"
              size="sm"
              onClick={() => void startSession(jobDescription, accessCode, language)}
              disabled={!jobDescription.trim()}
            >
              Retry
            </Button>
            {onReset && (
              <Button
                intent="secondary"
                type="outlined"
                size="sm"
                onClick={() => {
                  resetSession();
                  onReset();
                }}
              >
                New Role
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
